import React, { useState } from 'react';
import { Shield, X, Loader2 } from '../DeskeyIcons';
import { Button } from './button';
import { Input } from './input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TwoFAConfirmModal({ open, onClose, onConfirm, title, description, accent }) {
  const A = accent || '#ef4444';
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('reason'); // reason → code
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [devCode, setDevCode] = useState('');

  if (!open) return null;

  const sendCode = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('desky_token');
      const res = await fetch(`${API_URL}/api/auth/2fa/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      }).then(r => r.json());
      if (res.dev_code) setDevCode(res.dev_code);
      setStep('code');
      toast.success('Verification code sent');
    } catch {
      toast.error('Failed to send code');
    }
    setSending(false);
  };

  const handleConfirm = async () => {
    if (!code.trim() || code.length < 6) { toast.error('Enter 6-digit code'); return; }
    setVerifying(true);
    try {
      await onConfirm(reason, code);
    } catch {
      toast.error('Verification failed');
    }
    setVerifying(false);
  };

  const handleClose = () => { setReason(''); setCode(''); setStep('reason'); setDevCode(''); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="twofa-confirm-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
            <Shield className="w-4.5 h-4.5 text-red-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{title || '2FA Required'}</h3>
            <p className="text-[11px] text-slate-400">{description || 'This action requires verification'}</p>
          </div>
          <button onClick={handleClose} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="twofa-close">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {step === 'reason' && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Reason</label>
                <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for this action..." className="h-10 rounded-lg text-sm" data-testid="twofa-reason" />
              </div>
              <Button onClick={sendCode} disabled={!reason.trim() || sending} className="w-full h-10 rounded-lg text-white text-sm font-semibold" style={{ background: A }} data-testid="twofa-send-code">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Shield className="w-4 h-4 mr-1.5" />}
                {sending ? 'Sending...' : 'Send Verification Code'}
              </Button>
            </>
          )}

          {step === 'code' && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">6-Digit Code</label>
                <Input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter code" className="h-10 rounded-lg text-sm font-mono text-center tracking-[0.3em]" maxLength={6} data-testid="twofa-code-input" />
              </div>
              {devCode && <p className="text-[10px] text-slate-400 text-center">Dev mode: <span className="font-mono font-bold text-slate-600">{devCode}</span></p>}
              <Button onClick={handleConfirm} disabled={code.length < 6 || verifying} className="w-full h-10 rounded-lg text-white text-sm font-semibold" style={{ background: A }} data-testid="twofa-confirm-btn">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                {verifying ? 'Verifying...' : 'Confirm'}
              </Button>
              <button onClick={() => { setStep('reason'); setCode(''); setDevCode(''); }} className="w-full text-xs text-slate-400 hover:text-slate-600 text-center" data-testid="twofa-back">
                Back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
